import { useEffect } from 'react';
import { RecordListPayload, RecordListType, useDataContext } from 'context/DataStoreContext';
import { useApi } from 'model/Api';
import Pager from 'component/part/Pager';

const DEFAULT_LIMIT_OPTIONS = [10, 20, 50];
const FALLBACK_LIMIT = 10;

const style = {
  // List 全体: flex column。flex parent では空間を取り、それ以外では自然高さで振る舞う
  layout: {
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column' as const,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1rem',
    flexShrink: 0,
  },
  // テーブル領域: 親に高さの制約がある場合は overflow auto で内部スクロール、ない場合は自然高さで通常表示
  tableScrollArea: {
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'auto' as const,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    marginTop: '1rem',
    flexShrink: 0,
  },
  limitArea: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    fontSize: '1.4rem',
  },
  limitSelect: {
    padding: '0.4rem 0.6rem',
    fontSize: '1.4rem',
    borderRadius: '0.4rem',
    border: 'solid 0.1rem #cccccc',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
  },
  sorter: (order?: number, defaultOrder?: number) => ({
    cursor: 'pointer',
    ['&::after']: {
      content: `"${(order ?? defaultOrder) === 1 ? '▼' : '▲'}"`,
      color: order !== undefined ? '#000000' : '#cccccc',
    },
  }),
  table: {
    borderCollapse: 'collapse' as const,
    fontSize: '1.6rem',
  },
  th: {
    padding: '1rem',
    borderBottom: 'solid 0.1rem #cccccc',
    fontSize: '1.2rem',
    fontWeight: '700',
    textAlign: 'left' as const,
    verticalAlign: 'middle' as const,
    backgroundColor: 'white',
    // 縦スクロール時に見出し行を固定するため、thead 内 th を sticky 化
    position: 'sticky' as const,
    top: 0,
    zIndex: 2,
  },
  // stickyColumns 指定時に左端を固定するため、横方向の sticky と組み合わせて z-index を上げる
  stickyTh: {
    left: 0,
    zIndex: 3,
  },
  td: {
    padding: '1rem',
    borderBottom: 'solid 0.1rem #cccccc',
    verticalAlign: 'middle' as const,
  },
  stickyTd: {
    position: 'sticky' as const,
    left: 0,
    zIndex: 1,
    backgroundColor: 'white',
  },
  tableRow: {
    ['&:hover td']: { backgroundColor: '#f0f0f0' },
  },
  tableRowSelected: {
    ['& td']: { backgroundColor: '#ffcccc' },
    ['&:hover td']: { backgroundColor: '#ffcccc' },
  },
};

export type ListIndex = {
  label: string | string[];
  key?: string;
  align?: 'left' | 'right' | 'center';
};

/** rows に渡す行データ。配列または { cells, selected?, onClick? } のどちらでも可 */
export type ListRow =
  | React.ReactNode[]
  | { cells: React.ReactNode[]; selected?: boolean; onClick?: () => void };

/** 検索フォームの呼び出し元に渡される操作API */
export type ListSearchApi = {
  /** 検索条件を更新して1ページ目から再取得する。呼び出し元の検索フォームの実行ボタン等で呼ぶ */
  search: (conditions: RecordListPayload['conditions']) => void;
  /** 現在DataStoreContextに保持されている検索条件。フォームの初期値などに利用 */
  conditions: RecordListPayload['conditions'];
};

export type ListProps<T extends RecordListType> = Readonly<{
  type: T;
  indexes: ListIndex[];
  /** ページ側で常に固定で適用する条件（with指定など）。永続化されない */
  conditions?: RecordListPayload['conditions'];
  /** 永続化された検索条件が空のときに使う初期検索条件。例: ステータスのデフォルト絞り込み */
  defaultConditions?: RecordListPayload['conditions'];
  displayPager?: boolean;
  rows?: ListRow[];
  className?: string;
  /** 左端から固定表示にする列の数 */
  stickyColumns?: number;
  /** デフォルト表示数。state.limit がまだ未設定 (null) の場合に初回適用される */
  defaultLimit?: number;
  /** 表示数選択肢。指定がない場合は [10,20,50] */
  limitOptions?: number[];
  /** 検索フォーム要素を返す関数。引数の search を実行ボタン押下時に呼ぶことでAPI発動はListに委ねられる */
  children?: (api: ListSearchApi) => React.ReactNode;
}>;

const List = <T extends RecordListType>(props: ListProps<T>) => {
  const { state } = useDataContext();
  const api = useApi();
  const recordsState = state?.[props.type];
  const {
    page = 1,
    limit: stateLimit,
    perPage = 1,
    sorts = [],
  } = recordsState || {};
  const persistedConditions = recordsState?.conditions || {};
  const fixedConditions = props.conditions || {};
  const stickyColumns = props.stickyColumns ?? 0;
  // 表示数: 永続化された state.limit が優先、なければ defaultLimit、それもなければ最終フォールバック
  const effectiveLimit = stateLimit ?? props.defaultLimit ?? FALLBACK_LIMIT;
  const limitOptions = props.limitOptions ?? DEFAULT_LIMIT_OPTIONS;

  const indexes = props.indexes.map((indexParam) => {
    const sortParam = sorts.find(({ key }) => key === indexParam.key);
    const order = sortParam?.order;
    const defaultOrder = indexParam.key === 'id' ? 1 : 0;
    const label = Array.isArray(indexParam.label)
      ? indexParam.label.map((labelText) => <span>{labelText}</span>)
      : indexParam.label;
    return { ...indexParam, label, order, defaultOrder };
  });

  const callPaginate = (override?: Partial<RecordListPayload>) => {
    api[props.type].paginate({
      ...fixedConditions,
      conditions: persistedConditions,
      limit: effectiveLimit,
      sorts,
      page,
      ...(override || {}),
    });
  };

  const onChangePage = (nextPage: number) => callPaginate({ page: nextPage });

  const onChangeSort = (sortKey: string) => {
    const sortParamIndex = sorts.findIndex(({ key }) => key === sortKey);
    if (sortParamIndex >= 0) {
      const [sortParam] = sorts.splice(sortParamIndex, 1);
      sortParam.order = 1 - sortParam.order;
      sorts.unshift(sortParam);
    } else {
      sorts.unshift({ key: sortKey, order: 0 });
    }
    callPaginate({ sorts });
  };

  const onChangeLimit = (nextLimit: number) => {
    callPaginate({ limit: nextLimit, page: 1 });
  };

  const search: ListSearchApi['search'] = (newConditions) =>
    callPaginate({ conditions: newConditions, page: 1 });

  useEffect(() => {
    // 永続化された検索条件があればそれを優先、なければ defaultConditions を初期適用
    const initialConditions = Object.keys(persistedConditions).length > 0
      ? persistedConditions
      : (props.defaultConditions || {});
    callPaginate({ conditions: initialConditions });
  }, [props.conditions]);

  const showPager = props.displayPager !== false;

  return <div css={style.layout}>
    <div css={style.header}>
      {showPager
        ? <Pager onChange={(v) => onChangePage(v)} page={page} perPage={perPage} />
        : <span />}
      {props.children?.({ search, conditions: persistedConditions })}
    </div>
    <div css={style.tableScrollArea}>
      <table css={style.table} className={props.className}>
        <thead>
          <tr>
            {indexes.map((indexParam, i) => (
              <th
                key={i}
                css={[style.th, i < stickyColumns ? style.stickyTh : {}]}
              >
                <div css={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>{indexParam.label}</span>
                  {indexParam.key && (
                    <span
                      onClick={() => onChangeSort(indexParam.key as string)}
                      css={style.sorter(indexParam.order, indexParam.defaultOrder)}
                    />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(props.rows || []).map((row, rowIndex) => {
            const cells = Array.isArray(row) ? row : row.cells;
            const selected = Array.isArray(row) ? false : (row.selected ?? false);
            const onClick = Array.isArray(row) ? undefined : row.onClick;
            return (
              <tr
                key={rowIndex}
                onClick={onClick}
                css={[
                  style.tableRow,
                  selected ? style.tableRowSelected : {},
                  onClick ? { cursor: 'pointer' } : {},
                ]}
              >
                {cells.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    css={[
                      style.td,
                      cellIndex < stickyColumns ? style.stickyTd : {},
                      indexes[cellIndex]?.align ? { textAlign: indexes[cellIndex].align } : {},
                    ]}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    <div css={style.footer}>
      {showPager
        ? <Pager onChange={(v) => onChangePage(v)} page={page} perPage={perPage} />
        : <span />}
      <label css={style.limitArea}>
        <span>表示数</span>
        <select
          css={style.limitSelect}
          value={effectiveLimit}
          onChange={(e) => onChangeLimit(parseInt(e.target.value, 10))}
        >
          {limitOptions.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </label>
    </div>
  </div>;
};

export default List;
